import { forwardRef, Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Flex } from 'antd';
import { TbSearch } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { EmptySection } from '@/shared';
import { DropdownMenuLabel, DropdownPanel } from '@/shared/components/dropdowns';
import { ReportComponentType } from '@/types';

import MemoSlashCommandIcon from './MemoSlashCommandIcon';

export interface MemoSlashCommandItem {
    title: string;
    description?: string;
    section: string;
    shortcut?: string;
    keywords?: string[];
    iconKey?: string;
    reportComponentType?: ReportComponentType;
    command: (args: any) => void;
}

interface MemoSlashCommandsListProps {
    items: MemoSlashCommandItem[];
    query?: string;
    command: (item: MemoSlashCommandItem) => void;
}

const CommandList = forwardRef(({ items, query, command }: MemoSlashCommandsListProps, ref: any) => {
    const [search, setSearch] = useState(query ?? '');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const itemRefs = useRef<Record<number, HTMLElement | null>>({});

    const { token } = useAntDesignTheme();

    useEffect(() => {
        setSearch(query ?? '');
    }, [query]);

    const filteredItems = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        if (!normalizedSearch) {
            return items;
        }

        return items.filter((item) => {
            const searchableText = [item.title, item.description, item.section, ...(item.keywords ?? [])]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return searchableText.includes(normalizedSearch);
        });
    }, [items, search]);

    useEffect(() => {
        setSelectedIndex(0);
    }, [filteredItems]);

    useEffect(() => {
        itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex, filteredItems]);

    const groupedItems = useMemo(() => {
        return filteredItems.reduce(
            (accumulator, item) => {
                const existingGroup = accumulator.find((group) => group.section === item.section);

                if (existingGroup) {
                    existingGroup.items.push(item);
                    return accumulator;
                }

                accumulator.push({ section: item.section, items: [item] });
                return accumulator;
            },
            [] as Array<{ section: string; items: MemoSlashCommandItem[] }>,
        );
    }, [filteredItems]);

    const getItemIndex = (itemToFind: MemoSlashCommandItem) => filteredItems.findIndex((item) => item === itemToFind);

    const selectItem = (index: number) => {
        const item = filteredItems[index];

        if (item) {
            command(item);
        }
    };

    const upHandler = () => {
        if (!filteredItems.length) {
            return;
        }

        setSelectedIndex((selectedIndex + filteredItems.length - 1) % filteredItems.length);
    };

    const downHandler = () => {
        if (!filteredItems.length) {
            return;
        }

        setSelectedIndex((selectedIndex + 1) % filteredItems.length);
    };

    const enterHandler = () => {
        selectItem(selectedIndex);
    };

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }: any) => {
            if (event.key === 'ArrowUp') {
                upHandler();
                return true;
            }

            if (event.key === 'ArrowDown') {
                downHandler();
                return true;
            }

            if (event.key === 'Enter') {
                enterHandler();
                return true;
            }

            return false;
        },
    }));

    const handleKeyboardNavigation = (event: any) => {
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            event.stopPropagation();
            upHandler();
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            event.stopPropagation();
            downHandler();
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            enterHandler();
        }
    };

    return (
        <DropdownPanel
            maxHeight={428}
            search={{
                ariaLabel: 'Search memo commands',
                autoFocus: true,
                iconColor: token.colorTextQuaternary,
                onKeyDown: handleKeyboardNavigation,
                placeholder: 'Search Commands...',
                setValue: setSearch,
                value: search,
            }}
            style={{ borderRadius: token.borderRadiusLG }}
            width={320}
        >
            {filteredItems.length ? (
                <>
                    {groupedItems.map((group, groupIndex) => (
                        <Fragment key={group.section}>
                            <DropdownMenuLabel
                                style={{
                                    borderTop: groupIndex === 0 ? undefined : `1px solid ${token.colorBorderSecondary}`,
                                    fontWeight: 400,
                                }}
                            >
                                {group.section}
                            </DropdownMenuLabel>
                            {group.items.map((item) => {
                                const itemIndex = getItemIndex(item);
                                const selected = itemIndex === selectedIndex;

                                return (
                                    <Flex
                                        key={`${item.section}-${item.title}`}
                                        ref={(element) => {
                                            itemRefs.current[itemIndex] = element;
                                        }}
                                        align='center'
                                        gap='small'
                                        onMouseEnter={() => setSelectedIndex(itemIndex)}
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            selectItem(itemIndex);
                                        }}
                                        style={{
                                            cursor: 'pointer',
                                            minHeight: 40,
                                            padding: '0 0.75rem',
                                            background: selected ? token.colorFillSecondary : undefined,
                                        }}
                                    >
                                        <MemoSlashCommandIcon item={item} />
                                        <Flex flex={1} style={{ minWidth: 0 }}>
                                            <div
                                                style={{
                                                    color: token.colorText,
                                                    fontSize: token.fontSize,
                                                    fontWeight: 400,
                                                    lineHeight: 1.25,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {item.title}
                                            </div>
                                        </Flex>
                                        {item.shortcut && (
                                            <small
                                                style={{
                                                    color: token.colorTextTertiary,
                                                    fontSize: token.fontSizeSM,
                                                    whiteSpace: 'nowrap',
                                                    letterSpacing: '0.175rem',
                                                }}
                                            >
                                                {item.shortcut}
                                            </small>
                                        )}
                                    </Flex>
                                );
                            })}
                        </Fragment>
                    ))}
                </>
            ) : (
                <EmptySection
                    height={16 * 10}
                    icon={<TbSearch size='75%' style={{ color: token.colorText }} />}
                    label='No Matching Actions'
                    description={
                        <>
                            No memo actions match <strong>{search}</strong>.
                        </>
                    }
                />
            )}
        </DropdownPanel>
    );
});

export default CommandList;
